import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, getDocs, doc, getDoc, updateDoc, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../util/auth';
import MobileBankTable from '../components/MobileBankTable';
import WebBankTable from '../components/WebBankTable';
import WebLayout from '../components/WebLayout';
import EditBankModal from '../components/EditBankModal';
import { updateBank, deleteBank, dismissWalkthrough } from '../utils/socialActions';
import styled from '@emotion/styled';
import { useS } from 'use-s-react';
import { store } from '../store';
import DriverTour from '../components/DriverTour';
import '../styles/driver-custom.css';

const BonusWatcherContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  margin: 0,
  padding: 0,
  paddingLeft: 0,
  boxSizing: 'border-box',
  borderLeft: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
  [theme.breakpoints.down('md')]: {
    paddingLeft: 0,
    borderLeft: 'none',
  },
}));

const ContentWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  margin: 0,
  padding: 0,
  boxSizing: 'border-box',
});

const BonusWatcher = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const auth = useAuth();
  const { user, isAdmin } = auth;
  
  // Add console log to debug admin status
  console.log('Auth user:', user);
  console.log('Is admin:', isAdmin);

  // Global state using use-s
  const [banks, setBanks] = useS(store.banks);
  const [loading, setLoading] = useS(store.loading);
  const [searchValue, setSearchValue] = useS(store.searchValue);
  const [bookmarkedBanks, setBookmarkedBanks] = useS(store.bookmarkedBanks);
  const [hiddenBanks, setHiddenBanks] = useS(store.archivedBanks);
  const [sortBy, setSortBy] = useS(store.sortBy);
  const [sortOrder, setSortOrder] = useS(store.sortOrder);
  const [selectedState, setSelectedState] = useS(store.selectedState);
  const [editModalOpen, setEditModalOpen] = useS(store.editModalOpen);
  const [selectedBank, setSelectedBank] = useS(store.selectedBank);

  // Local state
  const [userBonuses, setUserBonuses] = useS({});
  const [selectedFilter, setSelectedFilter] = useS('all');

  // Walkthrough tour state
  const [runTour, setRunTour] = useState(false);
  const tourStartedRef = useRef(false);

  // Desktop tour steps
  const desktopSteps = [
    {
      element: '.bonus-stats-section',
      title: 'Welcome to BonusWatcher!',
      content: 'This shows your total earnings and completed bonuses. Track your progress as you complete bank bonus requirements.',
      placement: 'bottom',
    },
    {
      element: '.search-filter-section',
      title: 'Search & Filter',
      content: 'Use these controls to find bonuses that match your criteria. Search by bank name, filter by state, difficulty level, or switch between table and grid views.',
      placement: 'bottom',
    },
    {
      element: '.bank-table-section',
      title: 'Available Bonuses',
      content: 'Here you\'ll find all available bank bonuses. Each row shows the bonus amount, difficulty level, success rate, and expiration date.',
      placement: 'top',
    },
    {
      element: '.bonus-link-button',
      title: 'Apply for Bonus',
      content: 'Click the "Bonus Link" button to visit the bank\'s official bonus page and start the application process.',
      placement: 'bottom',
    },
    {
      element: '.bank-row-example',
      title: 'View Details',
      content: 'This bank row shows all the key information. Click "Next" to view the full details page where you can start tracking your progress!',
      placement: 'top',
    },
  ];

  // Mobile tour steps
  const mobileSteps = [
    {
      element: '.mobile-stats-section',
      title: 'Welcome to BonusWatcher!',
      content: 'Track your total earnings and completed bonuses right here.',
      placement: 'bottom',
    },
    {
      element: '.mobile-search-section',
      title: 'Search & Filter',
      content: 'Search for bank bonuses and use filters to find the best options for you.',
      placement: 'bottom',
    },
    {
      element: '.mobile-bank-card',
      title: 'Bonus Cards',
      content: 'Each card shows the bonus amount, bank name, difficulty, and more. Tap to see full details!',
      placement: 'top',
    },
    {
      element: '.mobile-bonus-link',
      title: 'Apply for Bonus',
      content: 'Tap "Bonus Link" to visit the bank\'s official bonus page and start your application.',
      placement: 'top',
    },
    {
      element: '.mobile-bank-card',
      title: 'View Details',
      content: 'This bank card shows all the key information. Tap "Next" to view the full details page where you can start tracking your progress!',
      placement: 'top',
    },
  ];

  const [tourSteps] = useState(isMobile ? mobileSteps : desktopSteps);

  // Check if user has seen the walkthrough
  const { userData } = auth;

  // Load walkthrough status
  useEffect(() => {
    // Reset tourStartedRef if user resets the walkthrough (hasSeenWalkthrough goes from true to false)
    if (userData && !userData.hasSeenWalkthrough) {
      tourStartedRef.current = false;
    }

    if (userData && !userData.hasSeenWalkthrough && !loading && banks.length > 0 && !tourStartedRef.current) {
      // Mark that we've started the tour to prevent re-initialization
      tourStartedRef.current = true;

      // Delay to ensure all elements are rendered and animations complete
      const timer = setTimeout(() => {
        setRunTour(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [userData, isMobile, loading, banks]);

  // Handle walkthrough callback
  const handleJoyrideCallback = useCallback(async (data) => {
    const { status, action, type, index } = data;
    const finishedStatuses = ['finished', 'skipped'];

    // Navigate to bank details when user clicks Next on the last step
    const isLastStep = isMobile ? index === 4 : index === 4;
    if (type === 'step:after' && isLastStep && action === 'next') {
      // Get the third bank (index 2) that we're highlighting
      if (banks && banks.length > 2) {
        const highlightedBank = banks[2];
        // Set flag to continue tour on bank details page
        sessionStorage.setItem('continueWalkthrough', 'true');
        // Navigate to the bank details page
        navigate(`/bank/${highlightedBank.id}`);
      }
    }

    if (finishedStatuses.includes(status)) {
      setRunTour(false);

      // If user skipped, always dismiss walkthrough immediately
      if (status === 'skipped') {
        sessionStorage.removeItem('continueWalkthrough');
        if (user?.uid) {
          try {
            await dismissWalkthrough(user.uid);
            // Refresh user data to update hasSeenWalkthrough flag
            await auth.refreshUserData();
          } catch (error) {
            console.error('Error dismissing walkthrough:', error);
          }
        }
      }
      // If user finished (clicked through all steps), only dismiss if they didn't continue to details page
      else if (status === 'finished') {
        const continuedToDetails = sessionStorage.getItem('continueWalkthrough');
        if (!continuedToDetails && user?.uid) {
          try {
            await dismissWalkthrough(user.uid);
            // Refresh user data to update hasSeenWalkthrough flag
            await auth.refreshUserData();
          } catch (error) {
            console.error('Error dismissing walkthrough:', error);
          }
        }
      }
    }
  }, [user, isMobile, banks, navigate, auth]);

  // Function to convert state names to camel case
  const formatState = (state) => {
    if (state.toLowerCase() === 'nationwide') {
      return 'Nationwide';
    }
    return state.toUpperCase();
  };

  // Load banks and user bonuses from Firestore
  const fetchBanksAndBonuses = async () => {
    try {
      setLoading(true);
      
      // Fetch banks
      const banksRef = collection(db, 'banks');
      const banksQuery = query(banksRef);
      const banksSnapshot = await getDocs(banksQuery);
      
      const banksData = banksSnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Bonus Watcher - Bank:', data.name, 'Success Rate:', data.successRate);
        console.log('Bonus Watcher - Full metrics:', {
          totalAttempts: data.totalAttempts,
          successfulCompletions: data.successfulCompletions,
          failedCompletions: data.failedCompletions,
          successRate: data.successRate
        });
        return {
          id: doc.id,
          name: data.name || 'Unnamed Bank',
          amount: Number(data.amount) || 0,
          bonusAmount: Number(data.amount) || 0,
          directDepositAmount: Number(data.directDeposit) || 0,
          successRate: data.successRate || 0,
          timeframe: Number(data.timeframe || data.days || 0),
          states: Array.isArray(data.states)
            ? data.states.map(state => formatState(state))
            : [],
          isBookmarked: data.bookmarkedBy?.includes(user?.uid) || false,
          isHidden: data.hiddenBy?.includes(user?.uid) || false,
          createdBy: data.createdBy,
          status: data.status || 'pending',
          completedBy: data.completedBy || [],
          difficulty: data.difficulty || 'Medium',
          expiration: data.expiration || null,
          url: data.url || '',
          description: data.description || '',
          terms: data.terms || '',
          requirements: data.requirements || [],
          accountType: data.accountType || 'Checking',
          hardPull: data.hardPull || false,
          chexSystems: data.chexSystems || false
        };
      });

      // Fetch user bonuses if user is logged in
      let userBonusesData = {};
      if (user?.uid) {
        const userBonusesRef = collection(db, 'userBonuses');
        const userBonusesQuery = query(userBonusesRef, where('userId', '==', user.uid));
        const userBonusesSnapshot = await getDocs(userBonusesQuery);
        
        userBonusesData = userBonusesSnapshot.docs.reduce((acc, doc) => {
          const data = doc.data();
          if (data.bankId) {
            acc[data.bankId] = {
              ...data,
              id: doc.id,
              dateStarted: data.dateStarted,
              expectedPayoutDate: data.expectedPayoutDate
            };
          }
          return acc;
        }, {});
      }

      console.log('Processed banks data:', banksData);
      console.log('Banks with expiration dates:', banksData.filter(bank => bank.expiration));
      setBanks(banksData);
      setUserBonuses(userBonusesData);
      
      // Set initial bookmarked and hidden banks
      const initialBookmarked = banksData
        .filter(bank => bank.isBookmarked)
        .map(bank => bank.id);
      const initialHidden = banksData
        .filter(bank => bank.isHidden)
        .map(bank => bank.id);
      
      setBookmarkedBanks(initialBookmarked);
      setHiddenBanks(initialHidden);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update useEffect to use fetchBanksAndBonuses
  // Refresh when user changes
  useEffect(() => {
    fetchBanksAndBonuses();
  }, [user?.uid]);

  // Also refresh when navigating back to this page
  useEffect(() => {
    const handleFocus = () => {
      console.log('Page gained focus, refreshing bank data...');
      fetchBanksAndBonuses();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.uid]);

  // Handlers
  const handleBankClick = (bank) => {
    navigate(`/bank/${bank.id}`);
  };

  const handleBookmarkToggle = async (bank) => {
    try {
      if (!user?.uid) return;

      const isBookmarked = bookmarkedBanks.includes(bank.id);
      const newBookmarkedBanks = isBookmarked
        ? bookmarkedBanks.filter(id => id !== bank.id)
        : [...bookmarkedBanks, bank.id];
      
      setBookmarkedBanks(newBookmarkedBanks);

      // Update the bank document's bookmarkedBy array
      const bankRef = doc(db, 'banks', bank.id);
      const bankDoc = await getDoc(bankRef);
      const currentBookmarkedBy = bankDoc.data()?.bookmarkedBy || [];
      
      await updateDoc(bankRef, {
        bookmarkedBy: isBookmarked
          ? currentBookmarkedBy.filter(uid => uid !== user.uid)
          : [...currentBookmarkedBy, user.uid]
      });
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const handleHideToggle = async (bank) => {
    try {
      if (!user?.uid) return;

      const isHidden = hiddenBanks.includes(bank.id);
      const newHiddenBanks = isHidden
        ? hiddenBanks.filter(id => id !== bank.id)
        : [...hiddenBanks, bank.id];

      setHiddenBanks(newHiddenBanks);

      // Update the bank document's hiddenBy array
      const bankRef = doc(db, 'banks', bank.id);
      const bankDoc = await getDoc(bankRef);
      const currentHiddenBy = bankDoc.data()?.hiddenBy || [];

      await updateDoc(bankRef, {
        hiddenBy: isHidden
          ? currentHiddenBy.filter(uid => uid !== user.uid)
          : [...currentHiddenBy, user.uid]
      });
    } catch (error) {
      console.error('Error toggling hide:', error);
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleSort = (sortValue) => {
    setSortBy(sortValue);
    // Toggle sort order if clicking the same sort option
    if (sortBy === sortValue) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to descending for amount and success rate, ascending for name and expiration
      setSortOrder(sortValue === 'amount' || sortValue === 'successRate' ? 'desc' : 'asc');
    }
  };

  const handleFilter = () => {
    if (selectedFilter === 'all') {
      setSelectedFilter('bookmarked');
    } else if (selectedFilter === 'bookmarked') {
      setSelectedFilter('hidden');
    } else {
      setSelectedFilter('all');
    }
  };

  // Filter and sort banks
  const filteredBanks = banks
    .filter(bank => {
      // Search filter
      if (searchValue) {
        const searchLower = searchValue.toLowerCase();
        return (
          bank.name.toLowerCase().includes(searchLower) ||
          bank.states.some(state => state.toLowerCase().includes(searchLower))
        );
      }
      
      // State filter
      if (selectedState) {
        return bank.states.includes(selectedState);
      }
      
      // Status filter
      if (selectedFilter === 'bookmarked') {
        return bookmarkedBanks.includes(bank.id);
      }
      if (selectedFilter === 'hidden') {
        return hiddenBanks.includes(bank.id);
      }
      
      return !hiddenBanks.includes(bank.id);
    })
    .sort((a, b) => {
      // If a sort option is selected or there's a filter active, don't prioritize bookmarks
      const isFilterActive = selectedState || searchValue || selectedFilter !== 'all';
      const aBookmarked = bookmarkedBanks.includes(a.id);
      const bBookmarked = bookmarkedBanks.includes(b.id);

      // If no specific sort is selected, or it's the default amount sort with no filters
      if (!sortBy || (!isFilterActive && sortBy === 'amount')) {
        // Sort by bookmarked status first
        if (aBookmarked !== bBookmarked) {
          return bBookmarked ? 1 : -1;
        }
        // Then by bonus amount
        return b.bonusAmount - a.bonusAmount;
      }

      // Apply the selected sort
      let comparison = 0;
      switch (sortBy) {
        case 'amount':
          comparison = b.bonusAmount - a.bonusAmount;
          break;
        case 'name':
          comparison = (a.name || '').toLowerCase().trim()
            .localeCompare((b.name || '').toLowerCase().trim());
          break;
        case 'successRate':
          comparison = b.successRate - a.successRate;
          break;
        case 'expiration':
          // Handle null/undefined expiration dates
          if (!a.expiration && !b.expiration) return 0;
          if (!a.expiration) return 1;  // Move items without expiration to the end
          if (!b.expiration) return -1;
          // Convert string dates to Date objects for proper comparison
          const dateA = new Date(a.expiration);
          const dateB = new Date(b.expiration);
          comparison = dateA.getTime() - dateB.getTime();
          break;
        default:
          comparison = b.bonusAmount - a.bonusAmount;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const handleEditBank = (bank) => {
    setSelectedBank(bank);
    setEditModalOpen(true);
  };

  const handleSaveEdit = async (updatedBank) => {
    try {
      console.log('handleSaveEdit - Saving bank with data:', updatedBank);
      console.log('handleSaveEdit - URL field:', updatedBank.url);

      const bankRef = doc(db, 'banks', selectedBank.id);

      // Update the bank document
      await updateDoc(bankRef, {
        name: updatedBank.name,
        amount: Number(updatedBank.bonusAmount),
        bonusAmount: Number(updatedBank.bonusAmount),
        directDeposit: Number(updatedBank.directDepositAmount),
        directDepositAmount: Number(updatedBank.directDepositAmount),
        timeframe: Number(updatedBank.timeframe),
        requirements: updatedBank.requirements,
        states: updatedBank.states,
        difficulty: updatedBank.difficulty,
        description: updatedBank.description,
        terms: updatedBank.terms,
        url: updatedBank.url,
        expiration: updatedBank.expiration,
        hardPull: updatedBank.hardPull,
        chexSystems: updatedBank.chexSystems,
        accountType: updatedBank.accountType,
        updatedAt: new Date().toISOString()
      });

      // Refresh the banks list
      await fetchBanksAndBonuses();

      setEditModalOpen(false);
      setSelectedBank(null);
    } catch (error) {
      console.error('Error updating bank:', error);
    }
  };

  const handleDeleteBanks = async (bankIds) => {
    try {
      await Promise.all(bankIds.map(id => deleteBank(id)));
      // Refresh banks after deletion
      fetchBanksAndBonuses();
    } catch (error) {
      console.error('Error deleting banks:', error);
    }
  };

  // Check if user is admin or creator of a bank
  const canEdit = (bank) => {
    return isAdmin || bank.createdBy === user?.uid;
  };

  // Handle state filter
  const handleStateClick = (state) => {
    setSelectedState(state === selectedState ? '' : state);
  };

  const content = (
    <BonusWatcherContainer>
      {/* Walkthrough Tour */}
      <DriverTour
        steps={tourSteps}
        run={runTour}
        onCallback={handleJoyrideCallback}
        showProgress={true}
        showButtons={['next', 'previous', 'close']}
      />
      <ContentWrapper>
        {isMobile ? (
          <MobileBankTable
            banks={banks}
            onBankClick={handleBankClick}
            onBookmarkToggle={handleBookmarkToggle}
            onArchiveToggle={handleHideToggle}
            onSearch={handleSearch}
            onSort={handleSort}
            onFilter={handleFilter}
            onStateClick={handleStateClick}
            searchValue={searchValue}
            bookmarkedBanks={bookmarkedBanks}
            archivedBanks={hiddenBanks}
            loading={loading}
            isAdmin={isAdmin}
            onEditBank={handleEditBank}
            onDeleteBanks={handleDeleteBanks}
            selectedState={selectedState}
            userBonuses={userBonuses}
          />
        ) : (
          <WebBankTable
            banks={banks}
            onBankClick={handleBankClick}
            onBookmarkToggle={handleBookmarkToggle}
            onArchiveToggle={handleHideToggle}
            onSearch={handleSearch}
            onSort={handleSort}
            onFilter={handleFilter}
            onStateClick={handleStateClick}
            searchValue={searchValue}
            bookmarkedBanks={bookmarkedBanks}
            archivedBanks={hiddenBanks}
            loading={loading}
            isAdmin={isAdmin}
            onEditBank={handleEditBank}
            onDeleteBanks={handleDeleteBanks}
            selectedState={selectedState}
            userBonuses={userBonuses}
          />
        )}
      </ContentWrapper>

      <EditBankModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedBank(null);
        }}
        bank={selectedBank}
        onEdit={handleSaveEdit}
      />
    </BonusWatcherContainer>
  );

  return isMobile ? content : <WebLayout>{content}</WebLayout>;
};

export default BonusWatcher; 